// ── Core types for the Lacuna game ──

export type PlayerColor = 'black' | 'red';
export type PlayerSlot = 'player1' | 'player2';
export type GameStatus = 'waiting' | 'playing' | 'finished';
export type TurnPhase =
  | 'chooseTension'
  | 'chooseK'
  | 'placeNode'
  | 'drawStrands'
  | 'sealing';
export type VertexType = 'anchor' | 'node';

export interface Vertex {
  id: string;
  type: VertexType;
  x: number;
  y: number;
  sockets: number;
  free: number;
  createdTurn?: number;
}

export interface Strand {
  id: string;
  from: string;
  to: string;
  color: PlayerColor;
  turn: number;
  waypoints?: Array<{ x: number; y: number }>; // player-drawn path (if absent, straight line)
}

export interface LacunaRegion {
  id: string;
  color: PlayerColor;
  boundaryLength: number;
  turn: number;
  boundaryStrands: string[];
}

export interface PlayerInfo {
  uid: string;
  name: string;
  color: PlayerColor;
}

export interface TurnState {
  number: number;
  activePlayer: PlayerSlot;
  phase: TurnPhase;
  drawnTokens: [number, number] | null;
  chosenTension: number | null;
  chosenK: number | null;
  placedNode: string | null;
  drawnStrandTargets: string[];
}

export interface GameMeta {
  created: number;
  status: GameStatus;
  player1: PlayerInfo | null;
  player2: PlayerInfo | null;
  roomCode: string;
  pieRuleAvailable: boolean;
}

export interface BagState {
  sequence: number[];
  drawIndex: number;
}

export interface BoardState {
  vertices: Record<string, Vertex>;
  strands: Record<string, Strand>;
}

export interface HistoryEntry {
  turn: number;
  player: PlayerSlot;
  tension: { drawn: [number, number]; kept: number };
  k: number;
  node: string;
  strands: string[];
  sealed: string[];
}

export interface GameState {
  meta: GameMeta;
  bag: BagState;
  board: BoardState;
  turn: TurnState;
  lacunae: Record<string, LacunaRegion>;
  scores: Record<PlayerColor, number>;
  history: HistoryEntry[];
}

// ── Helpers ──

export function getActiveColor(state: GameState): PlayerColor {
  const slot = state.turn.activePlayer;
  const player = state.meta[slot];
  return player?.color ?? 'black';
}

export function getPlayerSlotByUid(state: GameState, uid: string): PlayerSlot | null {
  if (state.meta.player1?.uid === uid) return 'player1';
  if (state.meta.player2?.uid === uid) return 'player2';
  return null;
}

export function nextStrandId(board: BoardState): string {
  const ids = Object.keys(board.strands ?? {});
  const max = ids.reduce((m, id) => {
    const n = parseInt(id.replace('S', ''), 10);
    return isNaN(n) ? m : Math.max(m, n);
  }, 0);
  return `S${max + 1}`;
}

export function nextNodeId(board: BoardState): string {
  const ids = Object.keys(board.vertices ?? {}).filter(id => id.startsWith('N'));
  const max = ids.reduce((m, id) => {
    const n = parseInt(id.replace('N', ''), 10);
    return isNaN(n) ? m : Math.max(m, n);
  }, 0);
  return `N${max + 1}`;
}

export function nextLacunaId(lacunae: Record<string, LacunaRegion>): string {
  const ids = Object.keys(lacunae ?? {});
  const max = ids.reduce((m, id) => {
    const n = parseInt(id.replace('L', ''), 10);
    return isNaN(n) ? m : Math.max(m, n);
  }, 0);
  return `L${max + 1}`;
}
